// TODO P0.8 v1.5: CSV export from this modal (deferred per spec).
// TODO: Mobile-specific styling deferred per spec — desktop hover pattern only.

import React, { useState, useMemo } from "react";
import Modal from "../Modal";
import { formatDateShort } from "../../utils/formatters";
import type { Receipt, LeaderboardMetric } from "../../types";

// =============================================================================
// RECEIPTS MODAL
// =============================================================================
// PURPOSE: Leaderboard drill-down — shows per-cell receipts for a given rep + metric.
// CPC special case: renders two stacked tables (numerator cash / denominator show).
// ESC + click-outside handled by Modal.tsx.
// =============================================================================

interface ReceiptsModalProps {
  open: boolean;
  onClose: () => void;
  repName: string;
  metric: LeaderboardMetric;
  metricLabel: string; // human label e.g. "Cash Collected"
  formula: string;
  receipts: Receipt[] | undefined; // undefined = loading
}

type SortCol = "label" | "value" | "date";
type SortDir = "asc" | "desc";

// Parse a display value string to a float for numeric sort.
// Strips leading $, commas, %, spaces. Falls back to NaN (→ lexicographic).
function parseNumeric(raw: string): number {
  const cleaned = raw.replace(/[$,%\s]/g, "");
  return parseFloat(cleaned);
}

function sortReceipts(rows: Receipt[], col: SortCol, dir: SortDir): Receipt[] {
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (col === "label") {
      return factor * a.label.localeCompare(b.label);
    }
    if (col === "value") {
      const na = parseNumeric(a.value);
      const nb = parseNumeric(b.value);
      if (!isNaN(na) && !isNaN(nb)) return factor * (na - nb);
      return factor * a.value.localeCompare(b.value);
    }
    // date: YYYY-MM-DD string — lexicographic sort is correct
    return factor * a.date.localeCompare(b.date);
  });
}

// ---------------------------------------------------------------------------
// Sub-component: a single sortable table
// ---------------------------------------------------------------------------

interface ReceiptTableProps {
  rows: Receipt[];
  tableLabel?: string; // heading above the table (CPC split case)
}

function ReceiptTable({ rows, tableLabel }: ReceiptTableProps): React.JSX.Element {
  const [sortCol, setSortCol] = useState<SortCol>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(col: SortCol) {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir(col === "date" ? "desc" : "asc");
    }
  }

  const sorted = useMemo(() => sortReceipts(rows, sortCol, sortDir), [rows, sortCol, sortDir]);

  function sortIcon(col: SortCol): string {
    if (col !== sortCol) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  const thClass =
    "text-left token-11 text-glass-muted font-semibold uppercase tracking-wide px-3 py-2 cursor-pointer select-none hover:text-glass transition-colors";

  return (
    <div className="mb-4">
      {tableLabel && (
        <p className="token-12 text-glass-secondary font-semibold mb-1 px-1">{tableLabel}</p>
      )}
      <div className="rounded-lg border border-black/[0.08] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[420px]">
            <thead className="bg-black/[0.03]">
              <tr>
                <th className={thClass} onClick={() => handleSort("label")}>
                  LEAD{sortIcon("label")}
                </th>
                <th className={thClass} onClick={() => handleSort("value")}>
                  VALUE{sortIcon("value")}
                </th>
                <th className={`${thClass} text-right`} onClick={() => handleSort("date")}>
                  DATE{sortIcon("date")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, idx) => (
                <tr
                  key={r.id}
                  className="border-t border-black/[0.05] hover:bg-black/[0.02] transition-colors anim-pop"
                  style={{
                    animationDelay: `${Math.min(idx, 15) * 100}ms`,
                    animationDuration: "480ms",
                  }}
                >
                  <td className="px-3 py-2 token-13 text-glass">{r.label}</td>
                  <td className="px-3 py-2 token-13 text-glass">
                    <span>{r.value}</span>
                    {r.sublabel && (
                      <span className="block token-11 text-glass-muted">{r.sublabel}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 token-13 text-glass text-right">
                    {formatDateShort(new Date(r.date + "T00:00:00"))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="token-11 text-glass-muted mt-1 px-1 text-right">
        {rows.length} {rows.length === 1 ? "entry" : "entries"}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ReceiptsModal({
  open,
  onClose,
  repName,
  metric,
  metricLabel,
  formula,
  receipts,
}: ReceiptsModalProps): React.JSX.Element | null {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  function handleCopy() {
    navigator.clipboard?.writeText(formula);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  // CPC split
  const isCPC = metric === "cpc";
  const cashRows =
    isCPC && Array.isArray(receipts) ? receipts.filter((r) => r.side === "cash") : [];
  const showRows =
    isCPC && Array.isArray(receipts) ? receipts.filter((r) => r.side === "show") : [];

  const modalTitle = `${metricLabel} — ${repName}`;

  return (
    <Modal isOpen={open} onClose={onClose} title={modalTitle} maxWidth="max-w-2xl">
      {/* Formula card — click to copy */}
      <button
        type="button"
        onClick={handleCopy}
        className="anim-tap w-full text-left p-3 rounded-lg bg-black/[0.03] hover:bg-black/[0.05] border border-black/[0.08] font-mono token-13 text-glass cursor-pointer mb-4"
        title="Click to copy formula"
      >
        {formula}
        {copied && <span className="ml-2 token-11 text-ds-green">copied</span>}
      </button>

      {/* Loading state */}
      {receipts === undefined && (
        <p className="text-glass-muted italic token-13">Loading receipts…</p>
      )}

      {/* Empty state */}
      {Array.isArray(receipts) && receipts.length === 0 && (
        <p className="text-glass-muted italic token-13">No receipts in current range</p>
      )}

      {/* CPC: two stacked tables */}
      {isCPC && Array.isArray(receipts) && receipts.length > 0 && (
        <>
          <ReceiptTable rows={cashRows} tableLabel="Cash receipts (numerator)" />
          <ReceiptTable rows={showRows} tableLabel="Show receipts (denominator)" />
          <div className="flex items-center justify-between mt-2 pt-3 border-t border-black/[0.06]">
            <span className="token-12 text-glass-muted">
              {cashRows.length} cash · {showRows.length} show
            </span>
            <button
              type="button"
              onClick={onClose}
              className="anim-tap px-4 py-1.5 rounded-lg bg-black/[0.05] hover:bg-black/[0.08] token-13 text-glass transition-colors"
            >
              Close
            </button>
          </div>
        </>
      )}

      {/* Default: single sortable table */}
      {!isCPC && Array.isArray(receipts) && receipts.length > 0 && (
        <>
          <ReceiptTable rows={receipts} />
          <div className="flex items-center justify-between mt-2 pt-3 border-t border-black/[0.06]">
            <span className="token-12 text-glass-muted">
              {receipts.length} {receipts.length === 1 ? "entry" : "entries"}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="anim-tap px-4 py-1.5 rounded-lg bg-black/[0.05] hover:bg-black/[0.08] token-13 text-glass transition-colors"
            >
              Close
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
